import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { useOrganizations } from '../../hooks/useFleet';

export type DuplicateEntityType = 'segment' | 'sequence' | 'playlist';

interface DuplicateToOrgDialogProps {
  open: boolean;
  onClose: () => void;
  entityType: DuplicateEntityType;
  entityId: string;
  entityName: string;
  currentOrganizationId?: string;
  onDuplicate: (params: { name?: string; targetOrganizationId?: string }) => Promise<void>;
}

export const DuplicateToOrgDialog: React.FC<DuplicateToOrgDialogProps> = ({
  open,
  onClose,
  entityType,
  entityId,
  entityName,
  currentOrganizationId,
  onDuplicate,
}) => {
  const [name, setName] = useState('');
  const [targetOrgId, setTargetOrgId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: orgsData, isLoading: orgsLoading } = useOrganizations({ size: 100 });
  const organizations = orgsData?.data || [];

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(`${entityName} (Copy)`);
      setTargetOrgId(currentOrganizationId || '');
      setError(null);
    }
  }, [open, entityName, currentOrganizationId]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await onDuplicate({
        name: name || undefined,
        targetOrganizationId: targetOrgId || undefined,
      });
      onClose();
    } catch (err) {
      setError((err as Error)?.message || 'Failed to duplicate');
    } finally {
      setIsSubmitting(false);
    }
  };

  const entityTypeLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ContentCopy />
        Duplicate {entityTypeLabel}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Create a copy of "{entityName}" in the selected organization.
          </Typography>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="New Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            helperText="Leave empty to use default name with (Copy) suffix"
          />

          <FormControl fullWidth>
            <InputLabel>Target Organization</InputLabel>
            <Select
              value={targetOrgId}
              label="Target Organization"
              onChange={(e) => setTargetOrgId(e.target.value)}
              disabled={orgsLoading}
            >
              {orgsLoading ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading organizations...
                </MenuItem>
              ) : (
                organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                    {org.id === currentOrganizationId && ' (current)'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || orgsLoading}
          startIcon={isSubmitting ? <CircularProgress size={16} /> : <ContentCopy />}
        >
          {isSubmitting ? 'Duplicating...' : 'Duplicate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
